import MonthlyDwellAnalytics from './MonthlyDwellChart';
import WeeklyDwellAnalytics from './WeeklyDwellChart';
import DailyDwellAnalytics from './DailyDwellChart';
import React, { useState, useEffect, useCallback, useRef, useMemo, Component } from 'react';
import '../../../../../styles/count_graphs.css'
import { url } from '../../../../../api/endpointURL';
import { BeatLoader } from 'react-spinners';
import { getAverageDwellTime, getDayIndex, getDwellTimePercentChange, filterMonthlyDwellData, filterDwellData, checkForNoDwellData } from '../baseSuiteUtils';


const DwellAnalyticsComponent = ({
  selectedSite,
  selectedCamera,
  selectedSuite,
  selectedYear,
  selectedCameraId,
  handleDwellDataState
}) => {
  const [monthlyDwellData, setMonthlyDwellData] = useState([]);
  const [monthlyDwellDataLoading, setMonthlyDwellDataLoading] = useState(false);
  const [weeklyDwellData, setWeeklyDwellData] = useState([]);
  const [weeklyDwellDataLoading, setWeeklyDwellDataLoading] = useState(false);

  const [dailyDwellData, setDailyDwellData] = useState([]);
  const [dailyDwellDataLoading, setDailyDwellDataLoading] = useState(false);

  const [selectedMonth, setSelectedMonth] = useState();
  const [selectedWeek, setSelectedWeek] = useState();
  const [selectedWeekOfMonth, setSelectedWeekOfMonth] = useState();
  const [selectedDay, setSelectedDay] = useState();
  const [selectedDayOfWeek, setSelectedDayOfWeek] = useState();
  const [selectedDayIndex, setSelectedDayIndex] = useState();

  const [currentAverageDwellTime, setCurrentAverageDwellTime] = useState();
  const [averageDwellTimeDiff, setAverageDwellTimeDiff] = useState();

  const [isMonthlyLineChart, setIsMonthlyLineChart] = useState(true);
  const [isWeeklyLineChart, setIsWeeklyLineChart] = useState(true);
  const [isDailyLineChart, setIsDailyLineChart] = useState(true);

  const [initialFetchCompleted, setInitialFetchCompleted] = useState(false);
  const [showDwell, setShowDwell] = useState(true);

  const prevCameraRef = useRef(selectedCameraId);

  const aggregatedMonthlyData = useMemo(() => 
    filterMonthlyDwellData(monthlyDwellData, selectedCameraId),
    [monthlyDwellData, selectedCameraId]
  );

  const aggregatedWeeklyData = useMemo(() => {
    // // console.log('\n=== AGGREGATING WEEKLY DATA ===');
    // // console.log('Raw weeklyDwellData:', weeklyDwellData);
    // // console.log('selectedCameraId:', selectedCameraId);
    
    if (!weeklyDwellData || weeklyDwellData.length === 0) {
        // // console.log('No weekly dwell data to aggregate');
        return [];
    }
    
    const result = filterDwellData(weeklyDwellData, selectedCameraId);
    // console.log('Aggregated weekly data result:', result);
    // console.log('Result structure:', result.map((week, i) => `Week ${i + 1}: ${week?.length || 0} items`));
    
    return result;
  }, [weeklyDwellData, selectedCameraId]);


  const aggregatedDailyData = useMemo(() => {
    // console.log('\n=== AGGREGATING DAILY DATA ===');
    // console.log('Raw dailyDwellData:', dailyDwellData);
    // console.log('selectedCameraId:', selectedCameraId);
    
    if (!dailyDwellData || dailyDwellData.length === 0) {
        // console.log('No daily dwell data to aggregate');
        return [];
    }
    
    const result = filterDwellData(dailyDwellData, selectedCameraId);
    // console.log('Aggregated daily data result:', result);
    console.log('Result structure:', result.map((day, i) => {
        const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        return `${dayNames[i]}: ${day?.length || 0} items`;
    }));
    
    return result;
  }, [dailyDwellData, selectedCameraId]);

  useEffect(() => {
    const cameraChanged = prevCameraRef.current !== selectedCameraId;
    prevCameraRef.current = selectedCameraId;

    if (cameraChanged) {
      setInitialFetchCompleted(false);
    }
  }, [selectedCameraId]);

  const hasActualData = useCallback(() => {
    const hasData = checkForNoDwellData(aggregatedMonthlyData);
    if (!aggregatedMonthlyData || !hasData) {
      return false;
    } else {
      return true;
    }
  }, [aggregatedMonthlyData, selectedCameraId]);

  useEffect(() => {
    // console.log('Month changed to:', selectedMonth);
    setWeeklyDwellData([]);
    setDailyDwellData([]);
    setSelectedWeek(null);
    setSelectedWeekOfMonth(null);
  }, [selectedMonth]);

  useEffect(() => {
    // console.log('=== WEEKLY DATA DEBUG ===');
    // console.log('1. Raw weeklyDwellData:', weeklyDwellData);
    // console.log('2. Aggregated weeklyDwellData:', aggregatedWeeklyData);
    // console.log('3. Selected camera ID:', selectedCameraId);
    
    // Check structure
    if (weeklyDwellData && weeklyDwellData.length > 0) {
        // console.log('4. First week raw:', weeklyDwellData[0]);
        if (weeklyDwellData[0] && weeklyDwellData[0].length > 0) {
            // console.log('5. First polygon in first week:', weeklyDwellData[0][0]);
            // console.log('   - camera_id:', weeklyDwellData[0][0].camera_id);
            // console.log('   - selectedCameraId:', selectedCameraId);
            // console.log('   - Match?', weeklyDwellData[0][0].camera_id === selectedCameraId);
        }
    }
    
    if (aggregatedWeeklyData && aggregatedWeeklyData.length > 0) {
        // console.log('6. First week aggregated:', aggregatedWeeklyData[0]);
    }
  }, [weeklyDwellData, aggregatedWeeklyData, selectedCameraId]);


  const getMonthName = (monthNumber) => {
    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    return months[monthNumber - 1];
  };

  const toggleLineChart = (period) => {
    switch(period) {
      case 'month':
        setIsMonthlyLineChart(!isMonthlyLineChart);
        break;
      case 'week':
        setIsWeeklyLineChart(!isWeeklyLineChart);
        break;
      case 'day':
        setIsDailyLineChart(!isDailyLineChart);
        break;
    }
  }

  const handleWeekSelection = useCallback((weekNumber) => {
    // console.log('Week selected:', weekNumber);
    setSelectedWeek(weekNumber);
    setSelectedWeekOfMonth(weekNumber);
  }, []);
  const handleDaySelection = useCallback((dayIndex, dayName) => {
    // console.log('Day selected:', dayName, 'Index:', dayIndex);
    setSelectedDayIndex(dayIndex);
    setSelectedDayOfWeek(dayName);
  }, []);

  const fetchMonthlyDwellData = useCallback(async () => {
    try {

      setMonthlyDwellDataLoading(true);

      const response = await fetch(`${url}/dwellTime/monthlyAverageDwellTime?year=${selectedYear}&hwid=${selectedSite?.hwid}`);
      const responseData = await response.json();

      setMonthlyDwellDataLoading(false);

      setMonthlyDwellData(responseData);


      if (responseData.length) {
        const latestMonthWithData = [...responseData]
          .reverse()
          .find(month => month.length > 0);

        // Only auto-select month on initial load or if no month is selected
        if (latestMonthWithData && !selectedMonth) {
          setSelectedMonth(latestMonthWithData[0].month);
        }

      }


      setInitialFetchCompleted(true);


    } catch (e) {
      // console.log(e)
    
    }
    setMonthlyDwellDataLoading(false);
  }, [selectedYear, selectedSite.hwid, selectedCameraId]);


  const fetchWeeklyDwellData = useCallback(async () => {
    try {
      // console.log('\n=== FETCHING WEEKLY DWELL DATA ===');
      // console.log('Year:', selectedYear, 'Month:', selectedMonth, 'HWID:', selectedSite?.hwid);

      setWeeklyDwellDataLoading(true);

      const response = await fetch(`${url}/dwellTime/weeklyAverageDwellTime?year=${selectedYear}&month=${selectedMonth}&hwid=${selectedSite?.hwid}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const responseData = await response.json();
      // console.log('Weekly API Response:', responseData);

      setWeeklyDwellData(responseData);

      // Look for weeks with data
      if (responseData && Array.isArray(responseData) && responseData.length > 0) {
        // console.log('Looking for weeks with data...');
        
        let latestWeekIndex = -1;
        let latestWeekData = null;
        
        for (let i = responseData.length - 1; i >= 0; i--) {
          const week = responseData[i];
          
          if (week && Array.isArray(week) && week.length > 0) {
            const validPolygon = week.find(polygon => 
              polygon && 
              polygon.polygon_id && 
              polygon.month_week &&
              typeof polygon.average_dwell_time === 'number'
            );
            
            if (validPolygon) {
              latestWeekIndex = i;
              latestWeekData = validPolygon;
              // console.log(`Found latest week with data: Week ${i + 1}`, validPolygon);
              break;
            }
          }
        }

        if (latestWeekIndex >= 0 && latestWeekData) {
          // console.log('✅ Setting selected week to:', latestWeekData.month_week);
          setSelectedWeek(latestWeekData.month_week);
          setSelectedWeekOfMonth(latestWeekData.month_week);
        } else {
          console.warn('❌ No weeks with valid data found');
          // DON'T set to null - keep previous values or set to 1
          setSelectedWeek(1);  // ← Changed: default to week 1
          setSelectedWeekOfMonth(1);  // ← Changed: default to week 1
        }
      } else {
        console.warn('❌ Weekly response was empty');
        setSelectedWeek(1);  // ← Changed: default to week 1
        setSelectedWeekOfMonth(1);  // ← Changed: default to week 1
      }
    } catch (error) {
      console.error('Error fetching weekly dwell data:', error);
      setSelectedWeek(1);  // ← Changed: default to week 1
      setSelectedWeekOfMonth(1);  // ← Changed: default to week 1
    } finally {
      setWeeklyDwellDataLoading(false);
    }
  }, [selectedYear, selectedMonth, selectedSite?.hwid]);

  const fetchDailyDwellData = useCallback(async () => {
    try {
      // console.log('\n=== CLIENT: Fetching Daily Dwell Data ===');
      // console.log('Year:', selectedYear);
      // console.log('Month:', selectedMonth);
      // console.log('Week (month_week):', selectedWeek);
      // console.log('HWID:', selectedSite?.hwid);

      if (!selectedWeek) {
        console.warn('No selected week, skipping daily fetch');
        return;
      }

      setDailyDwellDataLoading(true);

      const apiUrl = `${url}/dwellTime/dailyAverageDwellTime?year=${selectedYear}&month=${selectedMonth}&week=${selectedWeek}&hwid=${selectedSite?.hwid}`;
      // console.log('Daily API URL:', apiUrl);

      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const responseData = await response.json();
      // console.log('Daily response data:', responseData);
      console.log('Daily data structure:', responseData.map((day, i) => {
        const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        return `${dayNames[i]}: ${day.length} items`;
      }));

      setDailyDwellData(responseData);
    } catch (error) {
      console.error('Error fetching daily dwell data:', error);
      setDailyDwellData([]);
    } finally {
      setDailyDwellDataLoading(false);
    }
  }, [selectedYear, selectedMonth, selectedWeek, selectedSite?.hwid]);

  const getYearlyAverageDwellTime = (monthlyData) => {

    //Make this for one month
    let total = 0;
    let count = 0;

    for (let i = 0; i < monthlyData.length; i++) {
      const monthData = monthlyData[i];
      for (let j = 0; j < monthData.length; j ++) {
        total += monthData[j].total_dwell_time;
        count++;
      }
    }

    if (count > 0) {
      return ((total / 60 ) / count).toFixed(2);
    } else {
      return 0;
    }
  }


  const getAverageDwellTimePercentageChange = (currentMonthAverage, prevMonthAverage) => {


    let prevMonthAverageDwell = getYearlyAverageDwellTime(prevMonthAverage);
    let monthlyDiff = 0;

    if (prevMonthAverageDwell > 0) {


      monthlyDiff = ((currentMonthAverage - prevMonthAverageDwell) / prevMonthAverageDwell).toFixed(1) * 100;
      
      return monthlyDiff;
    } else {
      return '';
    }
  };

  const getAverageDwellForPeriod = (data, periodIndex) => {
      if (!data || !data[periodIndex] || data[periodIndex].length === 0) {
          return 0;
      }
      
      const periodData = data[periodIndex];
      const total = periodData.reduce((sum, item) => sum + (item.average_dwell_time || 0), 0);
      const count = periodData.length;
      
      return count > 0 ? total / count : 0;
  };

  // Calculate percentage change between two periods
  const calculatePercentChange = (currentValue, previousValue) => {
      if (!previousValue || previousValue === 0) {
          return 0;
      }
      
      const change = ((currentValue - previousValue) / previousValue) * 100;
      return Math.round(change * 10) / 10; // Round to 1 decimal place
  };

 // Get monthly percentage change
  const getMonthlyPercentChange = useCallback((monthIndex) => {
      // console.log('=== MONTHLY PERCENT CHANGE ===');
      // console.log('monthIndex:', monthIndex);
      // console.log('aggregatedMonthlyData:', aggregatedMonthlyData);
      
      if (monthIndex < 1 || !aggregatedMonthlyData || aggregatedMonthlyData.length === 0) {
          // console.log('Returning 0 - invalid index or no data');
          return 0;
      }
      
      const currentMonthAvg = getAverageDwellForPeriod(aggregatedMonthlyData, monthIndex);
      const previousMonthAvg = getAverageDwellForPeriod(aggregatedMonthlyData, monthIndex - 1);
      
      // console.log('Current month avg:', currentMonthAvg);
      // console.log('Previous month avg:', previousMonthAvg);
      
      const percentChange = calculatePercentChange(currentMonthAvg, previousMonthAvg);
      // console.log('Calculated percent change:', percentChange);
      
      return percentChange;
  }, [aggregatedMonthlyData]);

  // Get weekly percentage change
  const getWeeklyPercentChange = useCallback((weekIndex) => {
      // console.log('=== WEEKLY PERCENT CHANGE ===');
      // console.log('weekIndex:', weekIndex);
      
      if (weekIndex < 1 || !aggregatedWeeklyData || aggregatedWeeklyData.length === 0) {
          // console.log('Returning 0 - invalid index or no data');
          return 0;
      }
      
      const currentWeekAvg = getAverageDwellForPeriod(aggregatedWeeklyData, weekIndex);
      const previousWeekAvg = getAverageDwellForPeriod(aggregatedWeeklyData, weekIndex - 1);
      
      // console.log('Current week avg:', currentWeekAvg);
      // console.log('Previous week avg:', previousWeekAvg);
      
      const percentChange = calculatePercentChange(currentWeekAvg, previousWeekAvg);
      // console.log('Calculated percent change:', percentChange);
      
      return percentChange;
  }, [aggregatedWeeklyData]);

  // Get daily percentage change
  const getDailyPercentChange = useCallback((dayIndex) => {
      // console.log('=== DAILY PERCENT CHANGE ===');
      // console.log('dayIndex:', dayIndex);
      
      if (dayIndex === undefined || dayIndex === null || dayIndex < 1 || !aggregatedDailyData || aggregatedDailyData.length === 0) {
          // console.log('Returning 0 - invalid index or no data');
          return 0;
      }
      
      const currentDayAvg = getAverageDwellForPeriod(aggregatedDailyData, dayIndex);
      const previousDayAvg = getAverageDwellForPeriod(aggregatedDailyData, dayIndex - 1);
      
      // console.log('Current day avg:', currentDayAvg);
      // console.log('Previous day avg:', previousDayAvg);
      
      const percentChange = calculatePercentChange(currentDayAvg, previousDayAvg);
      // console.log('Calculated percent change:', percentChange);
      
      return percentChange;
  }, [aggregatedDailyData]);


  // Dwell time click handlers
  //--------------------------------------
  useEffect(() => {
    if (initialFetchCompleted) {
      const hasData = hasActualData();
      setShowDwell(hasData);

      if (handleDwellDataState) {
        handleDwellDataState(hasData);
      }
    }
  }, [initialFetchCompleted, hasActualData, handleDwellDataState]);
  //-------------------------------------------------------------------

  useEffect(() => {
    if (selectedYear && selectedSite?.hwid) {
      fetchMonthlyDwellData();
    }
  }, [selectedYear, selectedSite?.hwid, fetchMonthlyDwellData]);

  // Fetch weekly data when month changes (removed aggregatedMonthlyData from deps to prevent loop)
  useEffect(() => {
    if (selectedMonth > 0) {
      fetchWeeklyDwellData();
      const currentYearAverage = getYearlyAverageDwellTime(monthlyDwellData);
      setCurrentAverageDwellTime(currentYearAverage);
    }
  }, [selectedYear, selectedMonth, selectedSite?.hwid, fetchWeeklyDwellData, monthlyDwellData]);

  // Fetch daily data when week changes (removed aggregatedWeeklyData from deps to prevent loop)
  useEffect(() => {
    if (selectedWeek) {
      fetchDailyDwellData();
    }
  }, [selectedYear, selectedMonth, selectedWeek, selectedSite?.hwid, fetchDailyDwellData]);

  // Memoize percent change values to prevent recalculation on every render
  const monthlyIncrease = useMemo(() =>
    getMonthlyPercentChange(selectedMonth - 1),
    [getMonthlyPercentChange, selectedMonth]
  );

  const weeklyIncrease = useMemo(() =>
    getWeeklyPercentChange(selectedWeekOfMonth - 1),
    [getWeeklyPercentChange, selectedWeekOfMonth]
  );

  const dailyIncrease = useMemo(() =>
    getDailyPercentChange(selectedDayIndex),
    [getDailyPercentChange, selectedDayIndex]
  );

  // Memoize average values as well
  const monthlyAverage = useMemo(() =>
    getAverageDwellTime(aggregatedMonthlyData, selectedMonth - 1),
    [aggregatedMonthlyData, selectedMonth]
  );

  const weeklyAverage = useMemo(() =>
    getAverageDwellTime(aggregatedWeeklyData, selectedWeekOfMonth - 1),
    [aggregatedWeeklyData, selectedWeekOfMonth]
  );

  const dailyAverage = useMemo(() =>
    getAverageDwellTime(aggregatedDailyData, selectedDayIndex),
    [aggregatedDailyData, selectedDayIndex]
  );

  return (
    <>
      {showDwell ? (
        <>
          <div className='graphs-container'>
            <div style={{ width: '100%' }}>
              {aggregatedMonthlyData.length > 0 && (
                <MonthlyDwellAnalytics
                  selectedYear={selectedYear}
                  monthlyDwellData={aggregatedMonthlyData}
                  monthlyDataLoading={monthlyDwellDataLoading}
                  selectedMonth={selectedMonth}
                  setSelectedMonth={setSelectedMonth}
                  lineChart={isMonthlyLineChart}
                  monthlyAverage={monthlyAverage}
                  monthlyIncrease={monthlyIncrease}
                  isLineChart={isMonthlyLineChart}
                  toggleLineChart={toggleLineChart}
                />
              )}
            </div>

            <div style={{ width: '100%' }}>
              {selectedMonth && (
                <WeeklyDwellAnalytics
                  selectedYear={selectedYear}
                  weeklyDwellData={weeklyDwellData}
                  weeklyDwellDataLoading={weeklyDwellDataLoading}
                  selectedMonthName={getMonthName(selectedMonth)}
                  selectedWeek={selectedWeek}
                  selectedWeekOfMonth={selectedWeekOfMonth}
                  setSelectedWeek={handleWeekSelection}
                  weeklyAverage={weeklyAverage}
                  weeklyIncrease={weeklyIncrease}
                  isLineChart={isWeeklyLineChart}
                  toggleLineChart={toggleLineChart}
                />
              )}
            </div>

            <div style={{ width: '100%' }}>
              {selectedMonth && (
                <DailyDwellAnalytics
                  selectedYear={selectedYear}
                  dailyDwellData={dailyDwellData}
                  dailyDwellDataLoading={dailyDwellDataLoading}
                  selectedMonthName={getMonthName(selectedMonth)}
                  selectedWeekOfMonth={selectedWeekOfMonth}
                  setSelectedDay={handleDaySelection}
                  selectedDay={selectedDay}
                  selectedDayOfWeek={selectedDayOfWeek}
                  dailyAverage={dailyAverage}
                  dailyIncrease={dailyIncrease}
                  isLineChart={isDailyLineChart}
                  toggleLineChart={toggleLineChart}
                />
              )}
            </div>
          </div>
        </>
      ) : (
        <div style={{ display: 'none' }} />
      )}
    </>
  );
};

export default DwellAnalyticsComponent;
